import React, { useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { UNITS } from "../lib/constants";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Quick-add Product modal — opens inline on document form.
 * onCreated(product) is called after successful save so the caller can immediately pick the product.
 */
export default function QuickProductModal({ onClose, onCreated, initialName = "" }) {
  const [form, setForm] = useState({
    name: initialName, description: "", hsn: "", unit: "NOS",
    rate: 0, gst_rate: 18,
  });
  const [saving, setSaving] = useState(false);

  const lookupHsn = async () => {
    const code = (form.hsn || "").trim();
    if (!code || code.length < 2) return;
    try {
      const { data } = await api.get(`/lookup/hsn/${code}`);
      if (typeof data.gst_rate === "number") {
        setForm(f => ({ ...f, gst_rate: data.gst_rate }));
        toast.success(`GST ${data.gst_rate}% — ${data.description}`, { duration: 2200 });
      }
    } catch (err) { console.warn('HSN lookup failed; keeping current GST rate', err); }
  };

  const save = async () => {
    if (!form.name) return toast.error("Product name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        rate: Number(form.rate || 0),
        gst_rate: Number(form.gst_rate || 0),
      };
      const { data } = await api.post("/products", payload);
      toast.success("Product created");
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="quick-product-modal">
      <div className="bg-white rounded-lg border border-border max-w-xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="font-display text-lg font-semibold">Quick Add Product</div>
          <button onClick={onClose} className="btn-ghost" data-testid="close-quick-product"><X size={16} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <div className="label-overline block mb-1.5">Name *</div>
            <input className="field" autoFocus data-testid="qpr-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <div className="label-overline block mb-1.5">HSN / SAC</div>
            <input
              className="field font-mono"
              data-testid="qpr-hsn"
              value={form.hsn}
              onChange={e => setForm({ ...form, hsn: e.target.value })}
              onBlur={lookupHsn}
              placeholder="e.g. 84322100"
            />
          </div>
          <div>
            <div className="label-overline block mb-1.5">Unit</div>
            <select className="field" data-testid="qpr-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <div className="label-overline block mb-1.5">Selling Rate (₹)</div>
            <input type="number" step="0.01" className="field text-right" data-testid="qpr-rate" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} />
          </div>
          <div>
            <div className="label-overline block mb-1.5">GST Rate (%)</div>
            <select className="field" data-testid="qpr-gst" value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: Number(e.target.value) })}>
              {[0, 0.1, 0.25, 3, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <div className="label-overline block mb-1.5">Description (optional)</div>
            <textarea rows={2} className="field" data-testid="qpr-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted">
          <button onClick={onClose} className="btn-secondary" data-testid="cancel-quick-product">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="btn-primary" data-testid="save-quick-product">{saving ? "Saving…" : "Save Product"}</button>
        </div>
      </div>
    </div>
  );
}
